import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { RefreshCw, ArrowDownCircle, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Dialog to refund a booking's outstanding credit_balance back to the
 * customer's original payment method(s). Auto-refunds Stripe; flags
 * PayPal / cash / cheque / etransfer portions as "manual_pending" for
 * the admin to complete out-of-band. PIN-gated.
 */
export const RefundCreditDialog = ({ open, onClose, booking, onRefunded }) => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pin, setPin] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!open || !booking?.booking_id) return;
    setPin(''); setNote(''); setPlan(null);
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `${API_URL}/api/admin/bookings/${booking.booking_id}/refund-credit-preview`,
          { withCredentials: true }
        );
        setPlan(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Could not preview refund');
      } finally { setLoading(false); }
    })();
  }, [open, booking?.booking_id]);

  const submit = async () => {
    if (!pin.trim() || pin.trim().length !== 4) { toast.error('Enter a 4-digit PIN'); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/bookings/${booking.booking_id}/refund-credit`,
        { pin: pin.trim(), note: note.trim() },
        { withCredentials: true }
      );
      toast.success(r.data.message);
      onRefunded?.(r.data.booking);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refund failed');
    } finally { setSubmitting(false); }
  };

  const credit = Number(booking?.credit_balance || 0);
  const auto = (plan?.plan || []).filter((p) => p.refundable).reduce((s, p) => s + p.amount, 0);
  const manual = (plan?.plan || []).filter((p) => !p.refundable).reduce((s, p) => s + p.amount, 0);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="refund-credit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowDownCircle className="w-5 h-5 text-[#06D6A0]" />
            Refund credit balance
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Credit summary */}
          <div className="bg-gradient-to-br from-[#06D6A0]/10 to-[#FB923C]/5 border border-[#06D6A0]/30 rounded-xl p-4">
            <p className="text-xs text-[#6B6B7A] uppercase tracking-widest">Credit on file</p>
            <p className="text-3xl font-bold text-[#06D6A0]" data-testid="refund-credit-amount">${credit.toFixed(2)}</p>
            <p className="text-xs text-[#6B6B7A] mt-1">{booking?.booking_reference}</p>
          </div>

          {/* Plan */}
          {loading ? (
            <div className="text-center py-6 text-[#6B6B7A]"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
          ) : plan ? (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-[#1E1E24] uppercase tracking-widest">Refund plan</p>
              {plan.plan.length === 0 ? (
                <p className="text-sm text-[#FF3B6B]">No paid transactions found to refund against.</p>
              ) : (
                <ul className="space-y-2" data-testid="refund-plan-list">
                  {plan.plan.map((p) => (
                    <li
                      key={p.transaction_id}
                      className="border border-[#E8E5DF] rounded-xl p-3 flex items-start gap-3"
                      data-testid={`refund-plan-row-${p.transaction_id}`}
                    >
                      {p.refundable ? (
                        <CheckCircle2 className="w-4 h-4 text-[#06D6A0] mt-0.5 shrink-0" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-[#FB923C] mt-0.5 shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-semibold text-[#1E1E24] uppercase">{p.payment_method}</span>
                          <span className="text-sm font-bold text-[#FF3B6B]">−${p.amount.toFixed(2)}</span>
                        </div>
                        <p className="text-xs text-[#6B6B7A] mt-0.5">{p.note}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {plan.uncovered > 0 && (
                <p className="text-xs text-[#FF3B6B] flex items-center gap-1 mt-2">
                  <AlertTriangle className="w-3 h-3" />
                  ${plan.uncovered.toFixed(2)} of the credit can't be allocated — no matching paid transaction. You may need to issue this portion as a separate refund.
                </p>
              )}
              {(auto > 0 || manual > 0) && (
                <div className="flex gap-4 mt-3 text-xs">
                  {auto > 0 && <span className="text-[#06D6A0] font-semibold">Auto: ${auto.toFixed(2)}</span>}
                  {manual > 0 && <span className="text-[#FB923C] font-semibold">Manual: ${manual.toFixed(2)}</span>}
                </div>
              )}
            </div>
          ) : null}

          {/* PIN + note */}
          {plan?.plan?.length > 0 && (
            <div className="space-y-3 pt-3 border-t border-[#E8E5DF]">
              <div>
                <Label htmlFor="refund-note" className="text-xs">Note <span className="text-[#6B6B7A]">(optional)</span></Label>
                <Textarea
                  id="refund-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value.slice(0, 300))}
                  placeholder="e.g. Refunded after customer applied a gift card post-booking"
                  rows={2}
                  className="text-sm"
                  data-testid="refund-note"
                />
              </div>
              <div>
                <Label htmlFor="refund-pin" className="text-xs">Employee / Admin PIN</Label>
                <Input
                  id="refund-pin"
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="4-digit PIN"
                  className="text-center font-mono tracking-widest"
                  data-testid="refund-pin"
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="refund-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting || loading || !plan?.plan?.length}
            className="bg-[#06D6A0] hover:bg-[#05B88A] text-white"
            data-testid="refund-confirm"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            {submitting ? 'Processing…' : `Refund $${credit.toFixed(2)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RefundCreditDialog;
